import * as React from 'react';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, AppActionCreators, IAppState, MicrositeActionCreators, IMicrositeCampaignResponse, IMicrositeCampaignValidatedMailingListResponse } from '@nkr/store/index';
import { IBaseResponse, IMicrositeCampaignValidatedMailingList } from '@nkr/models/index';
import { Alert, Button, Col, ControlLabel, FormGroup, InputGroup, Panel, Row, Table, Well } from 'react-bootstrap';
import Dropzone, { DropEvent, FileRejection } from 'react-dropzone';
import CampaignSteps from './campaign-steps';
import { FormWrapper } from '../styles';
import { Container } from './styles';

import { toast } from 'react-toastify';
import { AxiosError } from 'axios';

interface IProps {
    campaignId: number,
    centerId: number,
    isAdmin: boolean,
    previousUrl: string
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getCampaign: (id: number) => Promise<IMicrositeCampaignResponse>,
    uploadSignatureFile: (id: number, file: File) => Promise<IBaseResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    id: number,
    campaignName: string,
    startDate: string,
    endDate: string,
    processedByNkr: boolean,
    campaignSignatureFileName: string,
    files: File[]
}

class UploadSignatureFile extends React.Component<TProps, IState> {
    readonly state: IState = {
        id: 0,
        campaignName: '',
        startDate: '',
        endDate: '',
        processedByNkr: true,
        campaignSignatureFileName: '',
        files: []
    }

    componentDidMount = () => {
        this.getData();
    }

    handleFileDropped = (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
        this.setState({
            files: acceptedFiles
        }, () => {
            this.getImgData();
        });
    }

    handleRemoveFile = () => {
        this.setState({
            files: []
        });
    }

    handleSubmit = (continueNextStep: boolean) => {
        this.props.showLoading('Uploading signature file, one moment please...').then(() => {
            this.props.uploadSignatureFile(this.state.id, this.state.files[0])
                .then(response => {
                    this.props.hideLoading();

                    if (response.data.success) {
                        toast.success('Signature file uploaded successfully, going to the letter edit step...', {
                            onClose: () => {
                                window.location.href = continueNextStep ? this.getNextUrl() : this.getReturnUrl();
                            }
                        });
                    }
                })
                .catch((error: AxiosError) => {
                    this.props.hideLoading();
                    toast.error('Unable to upload the signature file, the following error occurred: ' + error.message);
                });
        });
    }

    handleContinue = () => {
        let href = this.getNextUrl(); 
        window.location.href = href;     
    }

    handleCancel = () => {
        let href = this.getReturnUrl();
        window.location.href = href;
    }

    getNextUrl = () => {
        let href = this.props.isAdmin ? `/admin/microsite/campaign/${this.state.id}/edit-letter` : `/center/${this.props.centerId}/microsite/campaign/${this.state.id}/edit-letter`;
        return href;
    }

    getReturnUrl = () => {
        let href = this.props.isAdmin ? `/admin/microsite/campaigns` : `/center/${this.props.centerId}/microsite/campaigns`;
        return href;
    }

    getData = () => {
        this.props.showLoading('Getting the campaign, one moment please...').then(() => {
            this.props.getCampaign(this.props.campaignId).then((response) => {
                if (response.data.success) {
                    let { id, campaignName, startDate, endDate, processedByNkr, campaignSignatureFileName } = response.data.results

                    this.setState({
                        id,
                        campaignName,
                        startDate,
                        endDate,
                        processedByNkr,
                        campaignSignatureFileName
                    });
                }

                this.props.hideLoading();
            });
        });
    }

    getImgData = () => {
        const files = this.state.files[0];
        const imgPreview = document.getElementById("img-preview");

        if (files) {
            const fileReader = new FileReader();
            fileReader.readAsDataURL(files);
            fileReader.addEventListener("load", function () {
                imgPreview.style.display = "block";
                imgPreview.innerHTML = '<img src="' + this.result + '" style="width: 100%; height: 100%; object-fit: contain" />';
            });
        }
    }

    isValid = () => {
        return this.state.files.length > 0;
    }

    render = () => {
        const isValid = this.isValid();
        const isSuperAdmin = this.props.app.user.group.isSuper;
        
        return (
            <>
                <Row>
                    <Col xs={12} md={8} mdOffset={2}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, marginBottom: 25 }}>
                            <h1 className='text-left' style={{ margin: 0 }}>Upload Campaign Signature File</h1>
                            <div className='pull-right'>
                                <Button bsStyle='link' onClick={this.handleCancel}>Return to Campaign List</Button>
                            </div>
                        </div>
                        
                        <Well>
                            <Row>
                                <Col xs={12} sm={4} componentClass={ControlLabel}>Campaign:</Col>
                                <Col xs={12} lg={8}>{this.state.campaignName}</Col>
                            </Row>

                            <Row>
                                <Col xs={12} sm={4} componentClass={ControlLabel}>Campaign Type:</Col>
                                <Col xs={12} lg={8}>Mailed Out</Col>
                            </Row>

                            <Row>
                                <Col xs={12} sm={4} componentClass={ControlLabel}>NKR will process the mailing:</Col>
                                <Col xs={12} lg={8}>{this.state.processedByNkr ? 'Yes' : 'No'}</Col>
                            </Row>

                            <Row>
                                <Col xs={12} sm={4} componentClass={ControlLabel}>Acceptable file formats:</Col>
                                <Col xs={12} lg={8}>JPG, JPEG, PNG</Col>
                            </Row>

                            <Row>
                                <Col xs={12} sm={4} componentClass={ControlLabel}>Image dimensions:</Col>
                                <Col xs={12} lg={8}>We recommend that the signature image have maximum dimensions of 200 pixels (W) x 100 pixels (H)</Col>
                            </Row>

                            {this.state.campaignSignatureFileName != '' && (
                                <Row>
                                    <Col xs={12} sm={4} componentClass={ControlLabel}>Current Image:</Col>
                                    <Col xs={12} lg={8}>
                                        <div id='img-preview-existing' style={{ width: 200, height: 100, border: '1px solid #ccc', padding: 2, borderRadius: 4, marginRight: 10 }}>
                                            <img src={`${__API__}/microsite/campaign/${this.state.id}/signature-file`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                        </div>
                                    </Col>
                                </Row>
                            )}
                        </Well>

                        <div style={{ marginBottom: 20 }}>
                            <Dropzone
                                onDrop={this.handleFileDropped}
                                accept={['image/jpeg', 'image/png']}
                                noKeyboard
                            >
                                {({ getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject }) => {
                                    return (
                                        <Container {...getRootProps({ isDragActive, isDragAccept, isDragReject })}>
                                            <div style={{ width: '100%', height: 50, display: 'flex', alignItems: 'center' }}>
                                                <div style={{ margin: 'auto', textAlign: 'center' }}>
                                                    Click here to select the signature file or drag &amp; drop the signature file...
                                                </div>
                                            </div>
                                            <input {...getInputProps()} />
                                        </Container>
                                    );
                                }}
                            </Dropzone>

                            {this.state.files.length > 0 && (
                                <InputGroup>
                                    <InputGroup.Addon>File to upload:</InputGroup.Addon>
                                    <InputGroup.Addon style={{ width: 'initial', backgroundColor: 'white' }}>
                                        {this.state.files.map((file, index) =>
                                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                <div style={{ marginRight: 10, color: 'black' }}>{file.name}</div>
                                                <div id='img-preview' style={{ width: 200, height: 100, border: '1px solid #ccc', padding: 2, borderRadius: 4, marginRight: 10 }}></div>
                                                <div>
                                                    <Button bsStyle='danger' bsSize='xs' onClick={this.handleRemoveFile}>Remove</Button>
                                                </div>
                                            </div>
                                        )}
                                    </InputGroup.Addon>
                                </InputGroup>
                            )}
                        </div>

                        <Well bsSize='sm'>
                            <CampaignSteps currentStep={4} campaignId={this.props.campaignId} />
                        </Well>

                        <FormGroup>
                            <div className='col-xs-12 col-lg-12 text-center'>
                                {isSuperAdmin && <Button bsStyle='primary' style={{ marginRight: 10 }} disabled={!isValid} onClick={() => this.handleSubmit(true)}>Upload &amp; Continue</Button>}
                                <Button bsStyle={isSuperAdmin ? 'secondary' : 'primary'}  disabled={!isValid} onClick={() => this.handleSubmit(false)}>
                                    {`Upload ${isSuperAdmin ? 'Only' : 'And Finish Setup'}`}
                                </Button>
                                <Button bsStyle='link' onClick={this.handleContinue}>Continue</Button>
                                <Button bsStyle='link' onClick={this.handleCancel}>Cancel &amp; Return to the Campaign List</Button>
                            </div>
                        </FormGroup>
                    </Col>
                </Row>
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => { return dispatch(AppActionCreators.showLoadingAsync(message)); },
        hideLoading: () => { return dispatch(AppActionCreators.hideLoading()); },
        getCampaign: (id) => { return dispatch(MicrositeActionCreators.getCampaign(id, false)); },
        uploadSignatureFile: (id, file) => { return dispatch(MicrositeActionCreators.uploadSignatureFile(id, file)); }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(UploadSignatureFile);